import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRecoilState } from 'recoil';
import { authAtom, userAtom, roleAtom } from '../core/config/atoms';

import Lottie from 'lottie-react';
import { toast } from 'react-toastify';
import CryptoJS from 'crypto-js';
import { jwtDecode } from "jwt-decode";

import ServiceAuth from 'services/serviceAuth';
import ServiceUtil from 'services/serviceUtil';

import animBtnLoading from '../assets/anim/anim-btnLoading.json';
import bgLogin from '../assets/image/login.png';

export default function PageAuth() {

    const [authStatus, setAuthStatus] = useRecoilState(authAtom);
    const [roleData, setRoleData] = useRecoilState(roleAtom);
    const [, setUserData] = useRecoilState(userAtom);

    const [submit, setSubmit] = useState(false);

    const emailRef = useRef('');
    const passwordRef = useRef('');

    const navigate = useNavigate();
    const authService = new ServiceAuth();
    const utilService = new ServiceUtil();

    const decryptData = (encryptedData, iv) => {
        const keyWA = CryptoJS.enc.Hex.parse("b71c2e3ab56ff1a934a819e92618f26d02c7388a4e604d5b0c2c946e8d640c23");
        const ivWA = CryptoJS.enc.Base64.parse(iv);

        const cipherParams = CryptoJS.lib.CipherParams.create({
            ciphertext: CryptoJS.enc.Base64.parse(encryptedData)
        });

        const plain = CryptoJS.AES.decrypt(cipherParams, keyWA, { iv: ivWA });
        const decrypted = plain.toString(CryptoJS.enc.Utf8);
        return decrypted;
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmit(true);

        const email = emailRef.current.value;
        const password = passwordRef.current.value;

        if (/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email)) {
            if (password === "" || password === undefined) {
                toast.error('Password should not be empty !', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                setSubmit(false);
            } else {
                var body = {
                    email: email,
                    password: password
                }
                authService.staffLogin(body).then((res) => {
                    if (res.status) {
                        const decoded = jwtDecode(res.data.token);
                        const roles = decryptData(decoded.encrypted, decoded.iv);
                        setAuthStatus(true);
                        setUserData(res.data.user);
                        setRoleData(JSON.parse(roles));
                        utilService.saveEncrypted("user", res.data.user);
                        utilService.saveEncrypted("roles", JSON.parse(roles));
                        setSubmit(false);
                        navigate('/home');
                    } else {
                        toast.error('Error: ' + res.msg, { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                        setSubmit(false);
                    }
                });
            }
        } else {
            toast.error('Enter valid email id !', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            setSubmit(false);
        }
    }

    const checkUser = () => {
        if (roleData !== null && Object.keys(roleData).length !== 0) {
            navigate('/home');
        }
    }

    useEffect(() => {
        if (authStatus) {
            checkUser();
        }
    }, [authStatus, roleData, navigate]);

    return (
        <>
            <div className="w-screen h-screen overflow-hidden relative">
                <div className="xl:grid xl:grid-cols-12 xl:gap-0">
                    <div className="absolute xl:relative w-full xl:col-span-5 z-10">
                        <div className="h-screen flex flex-col items-center justify-center xl:bg-blue-50">
                            <div className="shadow-lg rounded-xl px-4 xl:px-10 py-20 xl:py-10 bg-white mt-6 mx-2 max-w-md md:w-auth w-11/12">
                                <h1 className="text-2xl text-black font-serif font-medium">Welcome back!</h1>
                                <h2 className="text-sm text-gray-400 font-serif">We`re so excited to see you again!</h2>
                                <form className="mt-8" onSubmit={handleSubmit}>
                                    <div className="flex flex-col">
                                        <label className="text-sm text-gray-900 font-serif">Email ID
                                            <sup className="text-red-600">*</sup>
                                        </label>
                                        <input type="email" placeholder="i.e. jhon.deo@gmail.com" className="w-full h-11 rounded bg-blue-50 px-4 py-2 text-sm font-sans mt-1" required autoComplete="off" ref={emailRef} />
                                    </div>
                                    <div className="flex flex-col mt-4">
                                        <label className="text-sm text-gray-900 font-serif">Password
                                            <sup className="text-red-600">*</sup>
                                        </label>
                                        <input type="password" placeholder="i.e. xxxxxxxx" className="w-full h-11 rounded bg-blue-50 px-4 py-2 text-sm font-sans mt-1" required autoComplete="off" ref={passwordRef} />
                                    </div>
                                    <button type="submit" className="mt-8 text-xs font-bold text-white rounded bg-prime w-full h-10 font-mono flex items-center justify-center" disabled={submit}>
                                        {submit && <Lottie animationData={animBtnLoading} className="w-8 h-8" />}
                                        {!submit && "SUBMIT"}
                                    </button>
                                    <p className='text-sm mt-4 text-center w-[240px] mx-auto'>By clicking continue, you agree to our <b><u>Terms of Service</u></b> and <b><u>Privacy Policy</u></b>.</p>
                                </form>
                                <div id="captcha"></div>
                                <div className="flex-grow"></div>
                                <p className="text-sm text-gray-400 font-serif text-center mt-16">&copy; 2025 MPJ Digital Kisaan E-Bazaar PVT LTD.</p>
                            </div>
                        </div>
                    </div>
                    <div className="h-screen xl:col-span-7 xl:flex items-center bg-prime">
                        <div className="w-full bg-pattern bg-[length:80%_80px] bg-repeat bg-center h-screen flex items-start lg:items-center justify-center relative p-[100px] overflow-hidden z-0">
                            <img src={bgLogin} alt="brand logo" className="w-[188px] h-full object-contain" />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}